import { useMemo } from 'react';
import { withStyles, InterestRate, formatAmount, MaterialSymbol } from 'vopay';

import { Title } from 'UI';
import styles from './AnnualRatesPopover.module.scss';
import { useTranslation } from 'react-i18next';
import { AccountType } from '../../AccountSummaryPage';

const cn = withStyles(styles);

type AnnualRatesPopoverProps = {
  accountType: AccountType;
  balance: number;
  interestRates: InterestRate[];
};

export default function AnnualRatesPopover({ accountType, balance, interestRates }: AnnualRatesPopoverProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances.AnnualRatesPopover' });

  const annualRates = interestRates.filter((rate) =>
    accountType === 'standard' ? rate.Term === 'short-term' : rate.Term === 'long-term',
  );

  //Filter out when both RangeStart and RangeEnd are invalid
  const filteredRates = useMemo(() => {
    return annualRates.filter(({ RangeStart, RangeEnd }) => {
      return RangeStart > 0 || RangeEnd > 0;
    });
  }, [annualRates]);

  return (
    <div className={cn('annualRatesPopover')}>
      <Title level="6">
        {accountType === 'standard'
          ? t('standardTitle', { defaultValue: 'Annual Standard Rates' })
          : t('premiumTitle', { defaultValue: 'Annual Premium Rates' })}
      </Title>

      <p className={cn('ratesInfo')}>
        {t('ratesInfo', { defaultValue: 'Earn more interest by adding funds & moving to the next tier.' })}
      </p>

      <div className={cn('grid')}>
        <div className={cn('row')}>
          <span>{t('range', { defaultValue: 'Range' })}</span>

          {accountType === 'standard' ? (
            <span className={cn('ratesTitle')}>
              {t('annualInterestRate', { defaultValue: 'Annual Interest Rate' })}
            </span>
          ) : (
            <div className={cn('premiumRatesTitle', 'blueBackground', 'blueFont')}>
              <div>
                {t('premium', { defaultValue: 'Premium' })}
                <span>({t('annually', { defaultValue: 'Annually' })})</span>
              </div>

              <MaterialSymbol icon="auto_awesome" fillAmount={1} />
            </div>
          )}
        </div>

        {filteredRates.map((rate) => {
          const rangeStart = rate.RangeStart === null ? 0 : rate.RangeStart;
          const rangeEnd = rate.RangeEnd === null ? Infinity : rate.RangeEnd;
          const isMatching = balance >= rangeStart && balance <= rangeEnd;
          const isUnlimitedRange = rate.RangeEnd === null;

          return (
            <div key={rate.RangeStart} className={cn('row', isMatching && 'blueFont')}>
              <div className={cn('range')}>
                <div>
                  CAD {formatAmount(rate.RangeStart)} {isUnlimitedRange && '+'}
                  {!isUnlimitedRange && ` - CAD ${formatAmount(rate.RangeEnd)}`}
                </div>

                {isMatching && (
                  <span className={cn('current')}>{t('currentPlan', { defaultValue: 'Current Plan' })}</span>
                )}
              </div>

              <span
                className={cn(
                  'rate',
                  isMatching && 'blueFont',
                  accountType === 'premium' && ['blueBackground', 'premiumRate', 'blueFont'],
                )}
              >
                {rate.AnnualInterestRate}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
